// =============================================================================
// celebration-sunnat.tsx — Sunnat to'y guest-page variant for sn01 "Sunnat".
//
// Cultural intent:
//   The sunnat to'yi is a major Uzbek family + community celebration around a
//   boy's circumcision. It is joyful and child-bright, but it is NOT a Western
//   birthday party. It is mahalla-driven: choyxona breakfast, the marosim
//   itself, quyma palov, children's games, a concert and tabriklar, then
//   sovg'a topshirish. Blessings (du'a) for the boy come first, not gifts.
//
//   This variant leans into that with:
//     * A vivid suzani-burst hero — a deterministic tessellation of octagon
//       stars + dotted rosettes orbits the boy's circular portrait, painted
//       in cream / terracotta / cobalt over a low-opacity Suzani ground.
//     * A SIGNATURE confetti layer of 30 small suzani-shaped pieces (octagon
//       stars + dotted circles) that gently fall through the hero on an 8s
//       loop. Reduced-motion: same pieces, no animation.
//     * A "Du'a wall" section unique to this variant — four traditional
//       Uzbek blessings on their own rows, separated by hand-drawn suzani
//       dividers, each row terracotta-accented.
//
// Sound triggers (5, all gated by useSound):
//   1. RSVP "yes" submit → chime, then pop ~140ms later (signature combo).
//   2. RSVP "no" submit → tap.
//   3. Any primary button click (du'a wall reveal toggle, schedule ICS button
//      via wrapper) → click.
//   4. Polaroid-style hero portrait hover → ding (one-shot per hover).
//   5. Section first-reveal on the du'a wall → swoosh.
// =============================================================================

import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import {
  CountdownSection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  ShareSection,
  GuestFooter,
  GuestPhoto,
} from "@/components/sections";
import * as O from "@/components/Ornaments";
import { useSound } from "@/lib/sound";
import { useT } from "@/lib/i18n";
import type { VariantProps } from "./types";
import { type LayoutCtx, type PhotoProps } from "@/components/layouts/_shared";

// Cobalt deep — the second suzani thread color. Mirrors layout.
const COBALT = "#1F3A8A";

// Re-export the layout types so the variant module exports them too — keeps
// import surface clean for any future caller that wants the LayoutCtx/Photo
// types alongside the variant. (Type-only re-exports are zero-cost.)
export type { LayoutCtx, PhotoProps };

// ─── Confetti pieces ────────────────────────────────────────────────────────
// Two suzani-shaped pieces: octagon star (8-point) and dotted ring. Painted
// in three colors so the falling layer feels like a real Uzbek embroidery
// burst rather than birthday confetti.
type SuzaniPieceShape = "octagon" | "ring";

interface SuzaniPiece {
  id: number;
  left: number;       // %
  size: number;       // px
  rot: number;        // deg
  delay: number;      // s (negative → mid-loop start)
  duration: number;   // s
  driftX: number;     // px sway
  color: string;
  shape: SuzaniPieceShape;
  variant: 0 | 1 | 2; // which keyframe path
}

function buildSuzaniPieces(n: number, accent: string): SuzaniPiece[] {
  // Deterministic so SSR and client agree.
  const rng = (seed: number) => {
    const x = Math.sin(seed * 9301 + 49297) * 233280;
    return x - Math.floor(x);
  };
  const palette = ["#FFF8E6", accent, COBALT];
  return Array.from({ length: n }).map((_, i) => ({
    id: i,
    left: rng(i + 1) * 100,
    size: 8 + rng(i + 200) * 12,
    rot: rng(i + 300) * 360,
    delay: -rng(i + 400) * 8,
    duration: 7 + rng(i + 500) * 4,
    driftX: (rng(i + 600) - 0.5) * 80,
    color: palette[i % palette.length],
    shape: i % 2 === 0 ? "octagon" : "ring",
    variant: (i % 3) as 0 | 1 | 2,
  }));
}

function SuzaniPieceEl({ piece }: { piece: SuzaniPiece }) {
  const { shape, size, color } = piece;
  if (shape === "octagon") {
    // 8-point star — tiny suzani rosette stamp
    const r = size / 2;
    const points = Array.from({ length: 16 })
      .map((_, i) => {
        const a = (i * 22.5 * Math.PI) / 180;
        const rr = i % 2 === 0 ? r : r * 0.45;
        return `${(r + Math.cos(a) * rr).toFixed(2)},${(r + Math.sin(a) * rr).toFixed(2)}`;
      })
      .join(" ");
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
        <polygon points={points} fill={color} opacity="0.9" />
        <circle cx={r} cy={r} r={Math.max(1, size * 0.12)} fill={color === "#FFF8E6" ? COBALT : "#FFF8E6"} />
      </svg>
    );
  }
  // dotted ring
  const r = size / 2;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
      <circle cx={r} cy={r} r={r - 1} fill="none" stroke={color} strokeWidth="1" />
      {Array.from({ length: 8 }).map((_, i) => {
        const a = (i * 45 * Math.PI) / 180;
        const cx = r + Math.cos(a) * (r - 2);
        const cy = r + Math.sin(a) * (r - 2);
        return <circle key={i} cx={cx} cy={cy} r={Math.max(1, size * 0.09)} fill={color} />;
      })}
    </svg>
  );
}

// ─── Reveal hook (IntersectionObserver) ─────────────────────────────────────
function useReveal<T extends HTMLElement>(onReveal?: () => void) {
  const ref = useRef<T | null>(null);
  const [shown, setShown] = useState(false);
  const cb = useRef(onReveal);
  cb.current = onReveal;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      setShown(true);
      cb.current?.();
      return;
    }
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      cb.current?.();
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            cb.current?.();
            io.disconnect();
          }
        });
      },
      { threshold: 0.18 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return { ref, shown };
}

// ─── Du'a wall row ──────────────────────────────────────────────────────────
function DuaRow({
  text,
  accent,
  ink,
  delay,
  shown,
}: {
  text: string;
  accent: string;
  ink: string;
  delay: number;
  shown: boolean;
}) {
  return (
    <div
      style={{
        textAlign: "center",
        padding: "28px 16px",
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(14px)",
        transition: `opacity 600ms ease ${delay}ms, transform 600ms cubic-bezier(0.34, 1.56, 0.64, 1) ${delay}ms`,
      }}
    >
      <span
        style={{
          fontFamily: "var(--font-display)",
          fontStyle: "italic",
          fontSize: "clamp(22px, 3.2vw, 32px)",
          lineHeight: 1.45,
          color: ink,
          fontWeight: 400,
        }}
      >
        <span aria-hidden style={{ color: accent, marginRight: 12 }}>❋</span>
        {text}
        <span aria-hidden style={{ color: accent, marginLeft: 12 }}>❋</span>
      </span>
    </div>
  );
}

// ─── Suzani divider — between du'a rows ─────────────────────────────────────
function SuzaniDivider({ accent, ink }: { accent: string; ink: string }) {
  return (
    <div
      aria-hidden
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        opacity: 0.85,
      }}
    >
      <span style={{ flex: 1, maxWidth: 100, height: 1, background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }} />
      <svg width="22" height="22" viewBox="0 0 22 22">
        <circle cx="11" cy="11" r="3" fill={accent} />
        <circle cx="11" cy="11" r="6" fill="none" stroke={ink} strokeWidth="0.6" opacity="0.55" />
        {[0, 60, 120, 180, 240, 300].map((d) => {
          const a = (d * Math.PI) / 180;
          const x = 11 + Math.cos(a) * 9;
          const y = 11 + Math.sin(a) * 9;
          return <circle key={d} cx={x} cy={y} r="0.9" fill={accent} />;
        })}
      </svg>
      <span style={{ flex: 1, maxWidth: 100, height: 1, background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }} />
    </div>
  );
}

// ─── Format date helper — "12.07.2026" → "12 IYUL 2026" ────────────────────
function formatUzDate(date: string): string {
  const months = [
    "yanvar", "fevral", "mart", "aprel", "may", "iyun",
    "iyul", "avgust", "sentabr", "oktabr", "noyabr", "dekabr",
  ];
  const [dd, mm, yyyy] = date.split(".");
  const idx = Math.max(0, Math.min(11, parseInt(mm, 10) - 1));
  return `${parseInt(dd, 10)}-${months[idx]} ${yyyy}`;
}

// ─── Main variant ───────────────────────────────────────────────────────────
export function CelebrationSunnatVariant({ t, isDark, ctx }: VariantProps) {
  const tt = useT();
  const { play } = useSound();
  const photos = t.photos || [];

  // Build the falling suzani confetti once per mount.
  const piecesRef = useRef<SuzaniPiece[]>(buildSuzaniPieces(30, t.accent));
  const pieces = piecesRef.current;

  // Prefers-reduced-motion gate.
  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduceMotion(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // Du'a wall reveal — plays a single swoosh on first visibility.
  const swooshFiredRef = useRef(false);
  const dua = useReveal<HTMLElement>(() => {
    if (swooshFiredRef.current) return;
    swooshFiredRef.current = true;
    play("swoosh");
  });

  // Hero portrait hover — fires a one-shot ding per hover.
  const portraitHoverRef = useRef(false);
  const onPortraitEnter = () => {
    if (portraitHoverRef.current) return;
    portraitHoverRef.current = true;
    play("ding");
  };
  const onPortraitLeave = () => {
    portraitHoverRef.current = false;
  };

  // Hero age — derive from t.names ("Behruz 7 yosh" / "Behruz · 7"). The
  // canonical sunnat age sits around 5–7 in UZ, so we default to 7 only when
  // the user hasn't supplied a number. Sn01 owners can edit names to change.
  const ageNumber = ((): number => {
    const m = t.names.match(/(\d{1,2})\s*(yosh|yil)?\s*$/i);
    return m ? parseInt(m[1], 10) : 7;
  })();

  // RSVP wrapper — chime + pop on yes, tap on no.
  const wrappedSetRsvp = (v: string | null) => {
    if (v === "yes") {
      play("chime");
      window.setTimeout(() => play("pop"), 140);
    } else if (v === "no") {
      play("tap");
    }
    ctx.rsvp.setRsvp(v);
  };

  const dateLine = useMemo(() => formatUzDate(t.date), [t.date]);

  // Du'a lines — traditional Uzbek blessings for the boy.
  const duaLines = [
    tt("variant.sn01.dua.0"),
    tt("variant.sn01.dua.1"),
    tt("variant.sn01.dua.2"),
    tt("variant.sn01.dua.3"),
  ];

  return (
    <div className="sn01-variant">
      <style>{`
        @keyframes sn01Fall0 {
          0%   { transform: translate3d(0, -10vh, 0) rotate(0deg); opacity: 0; }
          8%   { opacity: 0.85; }
          100% { transform: translate3d(var(--sn-dx), 110vh, 0) rotate(540deg); opacity: 0; }
        }
        @keyframes sn01Fall1 {
          0%   { transform: translate3d(0, -10vh, 0) rotate(0deg); opacity: 0; }
          10%  { opacity: 0.9; }
          100% { transform: translate3d(calc(var(--sn-dx) * -1), 110vh, 0) rotate(-460deg); opacity: 0; }
        }
        @keyframes sn01Fall2 {
          0%   { transform: translate3d(0, -10vh, 0) rotate(45deg); opacity: 0; }
          15%  { opacity: 0.8; }
          50%  { transform: translate3d(calc(var(--sn-dx) * 0.5), 50vh, 0) rotate(220deg); }
          100% { transform: translate3d(var(--sn-dx), 110vh, 0) rotate(440deg); opacity: 0; }
        }
        @keyframes sn01PortraitGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(200,128,75,0.0); }
          50%      { box-shadow: 0 0 0 14px rgba(200,128,75,0.18); }
        }
        @media (prefers-reduced-motion: reduce) {
          .sn01-piece, .sn01-portrait-ring { animation: none !important; }
        }
        @media (max-width: 768px) {
          .sn01-hero { padding: 64px 18px 48px !important; min-height: auto !important; }
          .sn01-portrait { max-width: 220px !important; border-width: 6px !important; }
          .sn01-names { font-size: clamp(36px, 12vw, 72px) !important; }
          .sn01-eyebrow { font-size: 10px !important; letter-spacing: 0.32em !important; }
          /* sn01-meta: allow date+venue to wrap on narrow screens so it
             doesn't overflow the 390px viewport. Also reduce letter-spacing
             so the inline-flex stays compact. */
          .sn01-meta {
            font-size: 12px !important;
            letter-spacing: 0.16em !important;
            flex-wrap: wrap !important;
            justify-content: center !important;
            gap: 8px !important;
            padding: 10px 16px !important;
            box-sizing: border-box !important;
            max-width: calc(100vw - 36px) !important;
          }
          .sn01-dua-section { padding: 64px 18px !important; }
          .sn01-variant button { min-height: 44px; }
        }
        .sn01-variant button:focus-visible { outline: 2px solid ${t.accent}; outline-offset: 2px; }
      `}</style>

      {/* ═══════════════════════════════════════════════════════════════════
          HERO — suzani burst + falling pieces + circular portrait
          ═══════════════════════════════════════════════════════════════════ */}
      <section
        className="sn01-hero"
        style={{
          position: "relative",
          minHeight: "100vh",
          padding: "100px 48px",
          textAlign: "center",
          background: t.bg,
          color: t.ink,
          overflow: "hidden",
        }}
      >
        {/* Low-opacity Suzani ground tessellation — a deterministic 4×4 grid
            of orbits painted at 8% opacity. Reads as a faint embroidered
            cloth behind the hero. */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            opacity: 0.09,
            pointerEvents: "none",
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gridTemplateRows: "repeat(4, 1fr)",
            color: t.accent,
          }}
        >
          {Array.from({ length: 16 }).map((_, i) => (
            <div key={i} style={{ display: "grid", placeItems: "center" }}>
              <O.Suzani size={140} color={i % 2 === 0 ? t.accent : COBALT} />
            </div>
          ))}
        </div>

        {/* Falling suzani-shaped confetti — 30 pieces, 8s loop */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            zIndex: 1,
            overflow: "hidden",
          }}
        >
          {pieces.map((p) => (
            <span
              key={p.id}
              className="sn01-piece"
              style={{
                position: "absolute",
                top: 0,
                left: `${p.left}%`,
                willChange: "transform, opacity",
                animation: reduceMotion
                  ? "none"
                  : `sn01Fall${p.variant} ${p.duration}s linear ${p.delay}s infinite`,
                transform: reduceMotion ? `translateY(${(p.id * 7) % 90}vh) rotate(${p.rot}deg)` : undefined,
                opacity: reduceMotion ? 0.7 : undefined,
                ["--sn-dx" as string]: `${p.driftX}px`,
              } as CSSProperties}
            >
              <SuzaniPieceEl piece={p} />
            </span>
          ))}
        </div>

        {/* Hero content (z-index above confetti) */}
        <div style={{ position: "relative", zIndex: 2, maxWidth: 720, margin: "0 auto" }}>
          {/* Eyebrow */}
          <div
            className="sn01-eyebrow"
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.42em",
              color: COBALT,
              marginBottom: 20,
            }}
          >
            {tt("variant.sn01.hero.eyebrow")}
          </div>

          {/* Names */}
          <h1
            className="sn01-names"
            style={{
              fontFamily: t.font || "var(--font-display)",
              fontSize: "clamp(72px, 12vw, 160px)",
              fontWeight: 400,
              lineHeight: 0.95,
              letterSpacing: "-0.01em",
              color: t.ink,
              marginBottom: 16,
            }}
          >
            {t.names}
          </h1>

          {/* Age line — italic terracotta */}
          <div
            style={{
              fontFamily: t.font || "var(--font-display)",
              fontStyle: "italic",
              fontSize: "clamp(22px, 3.4vw, 36px)",
              color: t.accent,
              marginBottom: 12,
            }}
          >
            {tt("variant.sn01.hero.age").replace("{n}", String(ageNumber))}
          </div>

          {/* Flourish */}
          <div style={{ display: "flex", justifyContent: "center", color: t.accent, marginBottom: 28 }} aria-hidden>
            <O.Flourish width={120} color={t.accent} />
          </div>

          {/* Portrait — circular embroidered hoop */}
          <div
            onMouseEnter={onPortraitEnter}
            onMouseLeave={onPortraitLeave}
            className="sn01-portrait sn01-portrait-ring"
            style={{
              width: "min(320px, 80vw)",
              aspectRatio: "1/1",
              margin: "0 auto 36px",
              position: "relative",
              borderRadius: "50%",
              overflow: "hidden",
              border: `8px solid ${t.accent}`,
              boxShadow: `0 0 0 4px ${t.bg}, 0 0 0 6px ${COBALT}77, 0 16px 0 -6px ${t.ink}22`,
              animation: reduceMotion ? "none" : "sn01PortraitGlow 4s ease-in-out infinite",
              cursor: "default",
            }}
          >
            <GuestPhoto
              src={photos[0]}
              style={{ width: "100%", height: "100%" }}
              fallbackBg={t.bg}
              fallbackTone={t.accent}
            />
          </div>

          {/* Date + venue */}
          <div
            className="sn01-meta"
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.32em",
              color: t.ink,
              textTransform: "uppercase",
              display: "inline-flex",
              alignItems: "center",
              gap: 12,
              padding: "12px 22px",
              background: t.bg,
              border: `1px solid ${t.accent}`,
              boxShadow: `4px 5px 0 -1px ${COBALT}66`,
            }}
          >
            <span>{dateLine}</span>
            <span style={{ color: t.accent }}>·</span>
            <span>{t.venue}</span>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          COUNTDOWN
          ═══════════════════════════════════════════════════════════════════ */}
      <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />

      {/* ═══════════════════════════════════════════════════════════════════
          DU'A WALL — unique to celebration-sunnat
          ═══════════════════════════════════════════════════════════════════ */}
      <section
        ref={dua.ref}
        className="sn01-dua-section"
        style={{
          padding: "120px 48px",
          borderTop: `1px solid ${t.ink}22`,
          background: `${t.accent}10`,
          textAlign: "center",
          position: "relative",
        }}
      >
        <div style={{ maxWidth: 760, margin: "0 auto" }}>
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.42em",
              color: COBALT,
              marginBottom: 16,
            }}
          >
            {tt("variant.sn01.dua.eyebrow")}
          </div>
          <h2
            style={{
              fontFamily: t.font || "var(--font-display)",
              fontStyle: "italic",
              fontSize: "clamp(36px, 5vw, 56px)",
              fontWeight: 400,
              marginBottom: 16,
              color: t.ink,
            }}
          >
            {tt("variant.sn01.dua.title")}
          </h2>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: 17,
              fontStyle: "italic",
              color: `${t.ink}cc`,
              marginBottom: 48,
            }}
          >
            {tt("variant.sn01.dua.intro")}
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {duaLines.map((line, i) => (
              <div key={i}>
                <DuaRow
                  text={line}
                  accent={t.accent}
                  ink={t.ink}
                  delay={120 * i}
                  shown={dua.shown}
                />
                {i < duaLines.length - 1 && <SuzaniDivider accent={t.accent} ink={t.ink} />}
              </div>
            ))}
          </div>

          {/* Closing flourish */}
          <div style={{ marginTop: 48, color: t.accent, display: "flex", justifyContent: "center" }} aria-hidden>
            <O.Flourish width={140} color={t.accent} />
          </div>
          <div
            style={{
              marginTop: 12,
              fontFamily: "var(--font-hand)",
              fontSize: 28,
              color: t.accent,
            }}
          >
            {tt("variant.sn01.dua.amen")}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          GALLERY — boy growing up
          ═══════════════════════════════════════════════════════════════════ */}
      <GallerySection t={t} />

      {/* ═══════════════════════════════════════════════════════════════════
          SCHEDULE
          ═══════════════════════════════════════════════════════════════════ */}
      <ScheduleSection
        t={t}
        items={[
          ["09:00", tt("variant.sn01.schedule.0.event")],
          ["10:00", tt("variant.sn01.schedule.1.event")],
          ["11:00", tt("variant.sn01.schedule.2.event")],
          ["12:30", tt("variant.sn01.schedule.3.event")],
          ["14:00", tt("variant.sn01.schedule.4.event")],
          ["16:00", tt("variant.sn01.schedule.5.event")],
        ]}
      />

      {/* ═══════════════════════════════════════════════════════════════════
          MAP
          ═══════════════════════════════════════════════════════════════════ */}
      <MapSection
        t={t}
        isDark={isDark}
        addressLine={tt("variant.sn01.map.address")}
      />

      {/* ═══════════════════════════════════════════════════════════════════
          RSVP
          ═══════════════════════════════════════════════════════════════════ */}
      <RsvpSection
        t={t}
        isDark={isDark}
        rsvp={ctx.rsvp.rsvp}
        setRsvp={wrappedSetRsvp}
        guests={ctx.rsvp.guests}
        setGuests={ctx.rsvp.setGuests}
        guestName={ctx.rsvp.guestName}
        setGuestName={ctx.rsvp.setGuestName}
        slug={ctx.slug}
        label={tt("variant.sn01.rsvp.label")}
      />

      {/* ═══════════════════════════════════════════════════════════════════
          SHARE + FOOTER
          ═══════════════════════════════════════════════════════════════════ */}
      <ShareSection t={t} />
      <GuestFooter t={t} />
    </div>
  );
}
